import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Phone, MessageSquare, Mail, Loader2, Star, MapPin, Globe, AlertCircle, ShieldCheck, AlertTriangle, RefreshCw, Instagram, Facebook, Trash2, Pencil, X, Save, Check, Pause, Play, Bot, ThumbsUp, ThumbsDown, Activity as ActivityIcon, ArrowRight, ArrowLeft as ArrowInbound, Ban } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import DecisionMakersCard from "../components/DecisionMakersCard";
import ActivityTimeline from "../components/ActivityTimeline";

const STAGES = ["scraped", "contacted", "answered", "qualified", "booked", "demo", "won", "lost"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actLoading, setActLoading] = useState({ classify: false, call: false, sms: false, verify: false, enrich: false });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data.lead);
      setActivities(data.activities);
      if (data.lead.campaign_id) {
        try {
          const { data: camps } = await api.get("/campaigns");
          setCampaign(camps.find((c) => c.id === data.lead.campaign_id) || null);
        } catch { /* ignore */ }
      } else {
        setCampaign(null);
      }
    } catch (e) {
      toast.error("Lead not found");
      navigate("/leads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const doClassify = async () => {
    setActLoading((s) => ({ ...s, classify: true }));
    try {
      await api.post(`/leads/${id}/ai-classify`);
      toast.success("AI classification complete");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Classification failed");
    } finally { setActLoading((s) => ({ ...s, classify: false })); }
  };

  const doCall = async () => {
    setActLoading((s) => ({ ...s, call: true }));
    try {
      const { data } = await api.post(`/leads/${id}/call`);
      toast.success(data.mocked ? "MOCK call initiated (add Vapi keys for real)" : "Call started");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Call failed");
    } finally { setActLoading((s) => ({ ...s, call: false })); }
  };

  const doSms = async () => {
    setActLoading((s) => ({ ...s, sms: true }));
    try {
      const { data } = await api.post(`/leads/${id}/sms`);
      toast.success(data.mocked ? "MOCK SMS sent (add Vapi keys for real)" : "SMS sent");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS failed");
    } finally { setActLoading((s) => ({ ...s, sms: false })); }
  };

  const doVerify = async () => {
    setActLoading((s) => ({ ...s, verify: true }));
    try {
      await api.post(`/leads/${id}/verify-place`);
      toast.success("Verified via Google Places");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Places verify failed");
    } finally { setActLoading((s) => ({ ...s, verify: false })); }
  };

  const doEnrich = async () => {
    setActLoading((s) => ({ ...s, enrich: true }));
    try {
      await api.post(`/leads/${id}/enrich-web`);
      toast.success("Website enriched");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Web enrich failed");
    } finally { setActLoading((s) => ({ ...s, enrich: false })); }
  };

  const doDelete = () => {
    setConfirmModal({
      title: "Delete lead",
      body: `Delete "${lead.name}"? This also removes its activity history. Cannot be undone.`,
      destructive: true,
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await api.delete(`/leads/${id}`);
          toast.success("Lead deleted");
          navigate("/leads");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const startEdit = () => {
    setEditForm({
      name: lead.name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      website: lead.website || "",
      address: lead.address || "",
      niche: lead.niche || "",
      city: lead.city || "",
      state: lead.state || "",
      sms_template: lead.sms_template || "",
      email_template: lead.email_template || "",
      call_script: lead.call_script || "",
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Only send fields the user actually changed
      const diff = {};
      Object.keys(editForm).forEach((k) => {
        if ((lead[k] || "") !== editForm[k]) diff[k] = editForm[k];
      });
      if (Object.keys(diff).length === 0) {
        toast.info("No changes to save");
        setEditing(false);
        setSaving(false);
        return;
      }
      await api.patch(`/leads/${id}`, diff);
      toast.success(`Lead updated (${Object.keys(diff).length} field${Object.keys(diff).length === 1 ? "" : "s"})`);
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setEdit = (k, v) => setEditForm((s) => ({ ...s, [k]: v }));

  const updateStage = async (stage) => {
    try {
      await api.patch(`/leads/${id}/stage`, { stage });
      toast.success(`Stage updated to ${stage}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const acceptQualification = async () => {
    try {
      await api.post(`/leads/${id}/accept-qualification`);
      toast.success("Stage updated · sequence paused");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const dismissQualification = async () => {
    try {
      await api.post(`/leads/${id}/dismiss-qualification`);
      toast.info("Dismissed — sequence continues");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const togglePause = async () => {
    try {
      const { data } = await api.post(`/leads/${id}/sequence-pause`, { paused: !lead.sequence_paused });
      toast.success(data.paused ? "Sequence paused" : "Sequence resumed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const startSequence = async () => {
    try {
      await api.post(`/leads/${id}/sequence-start`);
      toast.success("Multi-touch sequence started");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (loading || !lead) {
    return <div className="p-8 text-sm text-zinc-500 font-mono">Loading…</div>;
  }

  const labelColor =
    lead.ai_label === "Hot" ? "#EF4444"
    : lead.ai_label === "Warm" ? "#F59E0B"
    : lead.ai_label === "Cold" ? "#52525B"
    : "#0A0A0A";

  return (
    <div className="p-8 max-w-[1400px]" data-testid="lead-detail-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="border-b border-zinc-200 pb-6 mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="overline mb-2 flex items-center gap-3">
            <span>/ lead profile</span>
            {campaign && (
              <span
                className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#002FA7] text-white"
                data-testid="lead-campaign-badge"
              >
                {campaign.name}
              </span>
            )}
            {lead.do_not_contact && (
              <span
                className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-600 text-white inline-flex items-center gap-1"
                data-testid="lead-dnc-badge"
                title="This lead has unsubscribed. All outbound is blocked."
              >
                <Ban className="w-3 h-3" /> Unsubscribed · do not contact
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">{lead.name}</h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-zinc-600 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {lead.city}{lead.state ? `, ${lead.state}` : ""}</span>
            {lead.phone && (
              <span className="flex items-center gap-1 font-mono" data-testid="lead-phone">
                <Phone className="w-3.5 h-3.5" /> {lead.phone}
                {lead.phone_verified ? (
                  <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold text-emerald-700"><ShieldCheck className="w-3 h-3" /> verified</span>
                ) : (
                  <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold text-amber-600" title="AI-guessed — verify before mass-send"><AlertTriangle className="w-3 h-3" /> unverified</span>
                )}
              </span>
            )}
            {lead.email && <span className="flex items-center gap-1 font-mono" data-testid="lead-email"><Mail className="w-3.5 h-3.5" /> {lead.email}</span>}
            {lead.website && !lead.website.includes("google.com/maps") && (
              <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono hover:text-[#002FA7]">
                <Globe className="w-3.5 h-3.5" /> {lead.website.replace(/^https?:\/\//, "").slice(0, 30)}
              </a>
            )}
            {lead.website_quality === "bad" && <span className="inline-block text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 font-bold uppercase tracking-wider" data-testid="lead-quality-badge">⚑ Bad site — IDEAL TARGET</span>}
            {(!lead.website || lead.website.includes("google.com/maps") || lead.website_quality === "none") && <span className="inline-block text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 font-bold uppercase tracking-wider" data-testid="lead-quality-badge">⚑ No site — IDEAL TARGET</span>}
            {lead.rating && (
              <span className="flex items-center gap-1 font-mono">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {lead.rating}{lead.reviews ? ` (${lead.reviews})` : ""}
              </span>
            )}
            {lead.social_handles && lead.social_handles.instagram && (
              <a href={lead.social_handles.instagram} target="_blank" rel="noreferrer" className="text-pink-600" data-testid="lead-instagram"><Instagram className="w-4 h-4" /></a>
            )}
            {lead.social_handles && lead.social_handles.facebook && (
              <a href={lead.social_handles.facebook} target="_blank" rel="noreferrer" className="text-blue-600" data-testid="lead-facebook"><Facebook className="w-4 h-4" /></a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={lead.stage}
            onChange={(e) => updateStage(e.target.value)}
            data-testid="stage-select"
            className="border border-zinc-300 px-3 py-2 text-xs uppercase font-bold tracking-wider bg-white"
          >
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {!editing && (
            <button
              onClick={startEdit}
              data-testid="btn-edit-lead"
              className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              title="Edit lead fields"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          <button
            onClick={doDelete}
            data-testid="btn-delete-lead"
            className="border border-red-300 text-red-600 hover:bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
            title="Delete this lead"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {/* AI Qualification Suggestion Banner */}
      {lead.qualified_suggestion && (
        <div
          className={`mb-6 border-l-4 ${
            lead.qualified_suggestion.label === "interested"
              ? "border-emerald-500 bg-emerald-50/60"
              : lead.qualified_suggestion.label === "not_interested" || lead.qualified_suggestion.label === "unsubscribe"
                ? "border-red-500 bg-red-50/60"
                : "border-zinc-400 bg-zinc-50"
          } px-5 py-4 flex items-start gap-4`}
          data-testid="qualified-suggestion-banner"
        >
          <Bot className="w-5 h-5 mt-0.5 text-zinc-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">AI suggests:</span>
              <span
                className={`text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                  lead.qualified_suggestion.label === "interested"
                    ? "bg-emerald-600 text-white"
                    : lead.qualified_suggestion.label === "not_interested" || lead.qualified_suggestion.label === "unsubscribe"
                      ? "bg-red-600 text-white"
                      : "bg-zinc-600 text-white"
                }`}
                data-testid="suggestion-label"
              >
                {lead.qualified_suggestion.label.replace("_", " ")}
              </span>
              <span className="text-xs text-zinc-500 font-mono">
                · {Math.round((lead.qualified_suggestion.confidence || 0) * 100)}% confidence
              </span>
              <span className="text-[10px] text-zinc-400 font-mono">
                · via {lead.qualified_suggestion.source}
              </span>
            </div>
            {lead.qualified_suggestion.reasoning && (
              <div className="text-sm text-zinc-700 mb-2" data-testid="suggestion-reasoning">
                {lead.qualified_suggestion.reasoning}
              </div>
            )}
            {lead.qualified_suggestion.raw_excerpt && (
              <div className="text-xs text-zinc-500 italic border-l-2 border-zinc-300 pl-2 mb-3">
                {lead.qualified_suggestion.raw_excerpt}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={acceptQualification}
                data-testid="btn-accept-qualification"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
              >
                <ThumbsUp className="w-3.5 h-3.5" /> Accept (move stage + pause)
              </button>
              <button
                onClick={dismissQualification}
                data-testid="btn-dismiss-qualification"
                className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-4 py-1.5 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
              >
                <ThumbsDown className="w-3.5 h-3.5" /> Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sequence status bar */}
      {(lead.sequence_step != null || lead.next_action_at) && (
        <div className="mb-6 border border-zinc-200 bg-white px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="sequence-status-bar">
          <ActivityIcon className="w-4 h-4 text-zinc-500" />
          <div className="text-xs">
            <span className="font-bold uppercase tracking-wider text-zinc-500">Sequence:</span>
            <span className="ml-2 font-mono">
              {lead.sequence_paused
                ? "Paused"
                : lead.next_action_at
                  ? `next ${lead.next_action_kind || "?"} → ${new Date(lead.next_action_at).toLocaleString()}`
                  : (lead.sequence_step ?? 0) > 0 ? "Completed" : "Not started"}
            </span>
            {(lead.sequence_step ?? 0) > 0 && (
              <span className="ml-2 text-zinc-500">· step {lead.sequence_step}</span>
            )}
          </div>
          <div className="ml-auto flex gap-2">
            {(lead.sequence_step ?? 0) === 0 && !lead.sequence_paused && lead.phone && (
              <button
                onClick={startSequence}
                data-testid="btn-start-sequence"
                className="bg-[#002FA7] hover:bg-[#00227A] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
              >
                <Play className="w-3 h-3" /> Start sequence
              </button>
            )}
            {((lead.sequence_step ?? 0) > 0 || lead.next_action_at || lead.sequence_paused) && (
              <button
                onClick={togglePause}
                data-testid="btn-toggle-pause"
                className="border border-zinc-300 hover:bg-zinc-100 px-3 py-1.5 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
              >
                {lead.sequence_paused ? <><Play className="w-3 h-3" /> Resume</> : <><Pause className="w-3 h-3" /> Pause</>}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex gap-3 mb-8 flex-wrap">
        <button
          onClick={doClassify} disabled={actLoading.classify}
          data-testid="btn-classify"
          className="bg-black hover:bg-zinc-800 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {actLoading.classify ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {lead.ai_score != null ? "Re-Score" : "AI Score"}
        </button>
        <button
          onClick={doCall} disabled={actLoading.call || !lead.phone}
          data-testid="btn-call"
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {actLoading.call ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
          AI Call Lead
        </button>
        <button
          onClick={doSms} disabled={actLoading.sms || !lead.phone}
          data-testid="btn-sms"
          className="border-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {actLoading.sms ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
          Send SMS
        </button>
        <button
          onClick={doVerify} disabled={actLoading.verify}
          data-testid="btn-verify-place"
          className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-4 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          title="Re-verify phone, website, address via Google Places API"
        >
          {actLoading.verify ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          Verify
        </button>
        <button
          onClick={doEnrich} disabled={actLoading.enrich}
          data-testid="btn-enrich-web"
          className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-4 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          title="Re-fetch their website to extract email + social handles"
        >
          {actLoading.enrich ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-enrich
        </button>
      </div>

      {/* Decision-Makers / Contacts */}
      <DecisionMakersCard
        lead={lead}
        onChange={load}
        data-testid="decision-makers-card"
      />

      {/* Edit panel */}
      {editing && (
        <div className="mb-8 border-2 border-[#002FA7] bg-blue-50/30" data-testid="lead-edit-panel">
          <div className="px-5 py-3 border-b border-[#002FA7] bg-[#002FA7] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-widest">Editing Lead</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveEdit}
                disabled={saving}
                data-testid="btn-save-edit"
                className="bg-white text-[#002FA7] px-4 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-zinc-100 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                data-testid="btn-cancel-edit"
                className="border border-white/40 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-white/10"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditField label="Business Name" testid="edit-name">
              <input value={editForm.name} onChange={(e) => setEdit("name", e.target.value)} data-testid="input-edit-name" className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]" />
            </EditField>
            <EditField label="Phone" testid="edit-phone">
              <input value={editForm.phone} onChange={(e) => setEdit("phone", e.target.value)} data-testid="input-edit-phone" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]" placeholder="+1..." />
            </EditField>
            <EditField label="Email" testid="edit-email">
              <input value={editForm.email} onChange={(e) => setEdit("email", e.target.value)} data-testid="input-edit-email" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]" placeholder="contact@..." />
            </EditField>
            <EditField label="Website" testid="edit-website">
              <input value={editForm.website} onChange={(e) => setEdit("website", e.target.value)} data-testid="input-edit-website" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]" placeholder="https://..." />
            </EditField>
            <EditField label="Address" testid="edit-address">
              <input value={editForm.address} onChange={(e) => setEdit("address", e.target.value)} data-testid="input-edit-address" className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]" />
            </EditField>
            <EditField label="Niche" testid="edit-niche">
              <input value={editForm.niche} onChange={(e) => setEdit("niche", e.target.value)} data-testid="input-edit-niche" className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]" />
            </EditField>
            <EditField label="City" testid="edit-city">
              <input value={editForm.city} onChange={(e) => setEdit("city", e.target.value)} data-testid="input-edit-city" className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]" />
            </EditField>
            <EditField label="State" testid="edit-state">
              <input value={editForm.state} onChange={(e) => setEdit("state", e.target.value)} data-testid="input-edit-state" className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]" placeholder="TX, FL..." />
            </EditField>
            <EditField label="SMS Template" testid="edit-sms" full>
              <textarea value={editForm.sms_template} onChange={(e) => setEdit("sms_template", e.target.value)} data-testid="input-edit-sms" rows={3} className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7] resize-y" placeholder="Hi {name}, ..." />
            </EditField>
            <EditField label="Call Script (Opening Lines)" testid="edit-script" full>
              <textarea value={editForm.call_script} onChange={(e) => setEdit("call_script", e.target.value)} data-testid="input-edit-script" rows={4} className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7] resize-y" />
            </EditField>
            <EditField label="Email Template" testid="edit-email-tpl" full>
              <textarea value={editForm.email_template} onChange={(e) => setEdit("email_template", e.target.value)} data-testid="input-edit-email-tpl" rows={4} className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7] resize-y" />
            </EditField>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: AI Insights */}
        <div className="space-y-6">
          <div className="border border-zinc-200 bg-white">
            <div className="px-5 py-4 border-b border-zinc-200">
              <div className="overline">/ ai classification</div>
              <h2 className="font-heading text-lg font-bold mt-1">Score & Reasoning</h2>
            </div>
            <div className="p-5">
              {lead.ai_score != null ? (
                <>
                  <div className="flex items-baseline gap-3 mb-4">
                    <div className="font-heading text-5xl font-black">{lead.ai_score}</div>
                    <div className="font-mono text-xs text-zinc-500">/100</div>
                    <span className="ml-2 px-2 py-1 text-[10px] font-bold uppercase tracking-wider" style={{ background: labelColor, color: "white" }}>
                      {lead.ai_label}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-700 leading-relaxed" data-testid="ai-reasoning">
                    {lead.ai_reasoning}
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <AlertCircle className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
                  <div className="text-sm text-zinc-500">Not scored yet. Click &quot;AI Score&quot; above.</div>
                </div>
              )}
            </div>
          </div>

          {lead.sms_template && (
            <div className="border border-zinc-200 bg-white">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="overline">/ sms draft</div>
                  <h2 className="font-heading text-lg font-bold mt-1">First-Touch SMS</h2>
                </div>
                <MessageSquare className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="p-5 text-sm text-zinc-700 leading-relaxed font-mono whitespace-pre-wrap" data-testid="sms-template">
                {lead.sms_template}
              </div>
            </div>
          )}

          {lead.email_template && (
            <div className="border border-zinc-200 bg-white">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="overline">/ email draft</div>
                  <h2 className="font-heading text-lg font-bold mt-1">Cold Email</h2>
                </div>
                <Mail className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="p-5 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap" data-testid="email-template">
                {lead.email_template}
              </div>
            </div>
          )}
        </div>

        {/* Right: Call Script + Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {lead.call_script && (
            <div className="border border-zinc-200 bg-zinc-900 text-white">
              <div className="px-5 py-4 border-b border-zinc-700 flex items-center justify-between">
                <div>
                  <div className="overline" style={{ color: "#A1A1AA" }}>/ voice agent script</div>
                  <h2 className="font-heading text-lg font-bold mt-1">Opening Lines</h2>
                </div>
                <Phone className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="p-5 text-sm leading-relaxed font-mono whitespace-pre-wrap" data-testid="call-script">
                {lead.call_script}
              </div>
            </div>
          )}

          <div className="border border-zinc-200 bg-white" data-testid="conversation-card">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="overline">/ conversation</div>
                <h2 className="font-heading text-lg font-bold mt-1">Replies & Transcripts</h2>
              </div>
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                {activities.filter((a) => ["sms", "call"].includes(a.kind)).length} messages
              </span>
            </div>
            <ActivityTimeline activities={activities} leadName={lead.name} />
          </div>

          <div className="border border-zinc-200 bg-white">
            <div className="px-5 py-4 border-b border-zinc-200">
              <div className="overline">/ timeline</div>
              <h2 className="font-heading text-lg font-bold mt-1">Full Activity History</h2>
            </div>
            <div className="max-h-[500px] overflow-y-auto" data-testid="lead-activities">
              {activities.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-zinc-500">No activity yet.</div>
              ) : (
                activities.map((a) => {
                  const isInbound = a.direction === "inbound";
                  const isManual = a?.metadata?.manual;
                  const step = a?.metadata?.sequence_step;
                  return (
                    <div key={a.id} className="px-5 py-3 border-b border-zinc-100 last:border-0" data-testid={`activity-${a.id}`}>
                      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                            isInbound ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white"
                          }`}>
                            {isInbound ? "← in" : "out →"} {a.kind}
                          </span>
                          {isManual && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">
                              manual
                            </span>
                          )}
                          {!isManual && typeof step === "number" && step > 0 && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800">
                              step {step}
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                          {new Date(a.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm font-medium">{a.summary}</div>
                      {a.body && (
                        <div className="text-xs text-zinc-600 mt-1 font-mono whitespace-pre-wrap">{a.body}</div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}


function EditField({ label, children, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={`field-${testid}`}>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

